import { Tooltip } from '@grapecity/wijmo'
import { CollectionView } from '@grapecity/wijmo'
import { WjFlexGrid } from '@grapecity/wijmo.angular2.grid'
import {
    AllowDragging,
    AllowMerging,
    AllowSorting,
    CellRange,
    CellRangeEventArgs,
    CellType,
    Column,
    FlexGrid,
    GroupRow,
    HeadersVisibility,
    HitTestInfo,
    SelectionMode,
} from '@grapecity/wijmo.grid'
import { FlexGridFilter } from '@grapecity/wijmo.grid.filter'
import { Observable, forkJoin } from 'rxjs'

import { IRepository } from '../data'
import { IModalEditOptions, ModalBackDrop, ModalEditMode, ModalSize, RepositoryModal } from '../modals'
import { Services } from '../services'
import { DataEvent, Event, IDataEvent, IEvent } from '../utility'
import { Dictionary } from './dictionary'

export class Grid<T> {
    /**
     * @param {Services}  services - The direct injected services object.
     * @param {string} [keyName] - The property that is the key for the table. Default 'Id'
     * @param {string} [controller] - A reference to the component that can be used to get a reference to the component in wijmo event scopes.
     * @returns {void}
     */
    constructor(
        protected services: Services,
        public keyName = 'id',
        public controller: any = null,
    ) {
        this.filterRefresh = services.utility.debounce(350, () => {
            const currentSelectedItem = this.selectedItem
            this.initialSelectionSet = false
            this.refresh()

            if (currentSelectedItem != this.selectedItem) {
                this.onSelectionChanged(this.selectedItem)
            }

            this.onFilterChanged()
        })
    }
    private dragIndex = 0
    private _tooltip = new Tooltip()

    private _columnHeaderRowHeight!: number
    private _modalGetItemToAdd!: () => T
    private _modalComponent: any
    private _modalInitializeParams: any
    private _modalTypeName!: string
    private _modalSize!: ModalSize
    private _modalBackDrop!: ModalBackDrop
    private _modalRepository!: IRepository<T>
    private _hasColumnFilters = false
    private filterRefresh: () => void
    private initialSelectionSet = false
    private _isReadOnly = true
    private _doInitialSelection = true
    private _selectionMode = SelectionMode.CellRange
    private _allowDragging: AllowDragging = AllowDragging.Columns
    private _allowSorting: AllowSorting = AllowSorting.MultiColumn
    private _headerVisibility: HeadersVisibility = HeadersVisibility.Column
    private _autoGenerateColumns = false
    private _editOnDoubleClick = true
    private _showAggregateRow = false

    private _parentGrid!: Grid<any>
    private _parentSelectionChangedHandler: any = null
    private _getDetailItems!: (parentItem: any) => T[]

    private _detailsOfRepository: IRepository<T> | null = null
    private _getParentId!: (item: any) => any
    private _parentItemsFetched!: Dictionary<boolean>

    private _customDeletePrompt!: (item: any) => any | undefined
    private _customDeletePromptResolution!: (item: any) => any
    private _deleteConfirmTitle = 'Confirm Delete'
    private _deleteConfirmMessage = 'Are you sure you want to delete the selected item?'
    private _deleteConfirmMultiTitle = 'Multiple Item Delete'
    private _deleteConfirmMultiMessage = 'Are you sure you want to delete the selected items?'

    public isInitialized = false
    public flexGrid!: WjFlexGrid
    public canAdd = true
    public canEdit = true
    public canDelete = true
    public isLoadingData = false

    private _view = new CollectionView<any>([])
    public get view(): CollectionView<T> {
        return this._view
    }
    public set view(value) {
        this._view = value as any
    }

    public get selectedItem(): T | any {
        return this._view.currentItem
    }
    public set selectedItem(value: T | any) {
        try {
            this._view.moveCurrentTo(null) // clear multi-selected items
            this._view.moveCurrentTo(value)

            if (this.flexGrid) {
                if (value == null) {
                    this.flexGrid.select(-1, -1)
                } else if (this.flexGrid.selection) {
                    setTimeout(() => {
                        this.flexGrid.scrollIntoView(this.flexGrid.selection.row, this.flexGrid.selection.col)
                    }, 10)
                }
            }
        } catch (err) {
            console.log('Grid selectItem set: ', err)
        }
    }
    public get itemsInSortedOrder(): T[] {
        return this._view.items as T[]
    }
    public get items(): T[] {
        return this._view.sourceCollection as T[]
    }
    public set items(value: T[]) {
        this.loadItems(value)
    }
    private loadItems(items: T[], keepOriginalSelection = false) {
        this.isLoadingData = true

        const key = this.selectedItem && this.keyName ? this.selectedItem[this.keyName] : undefined

        let previousSelection
        if (this.isInitialized) {
            previousSelection = this.flexGrid.selection
        }

        this.selectedItem = null
        this._view.beginUpdate()
        this._view.sourceCollection = items
        this._view.endUpdate()
        this.isLoadingData = false

        if (keepOriginalSelection) {
            if (previousSelection !== null) {
                this.flexGrid.selection = previousSelection
            }
        } else if (this.restoreSelection(key) && this.isInitialized) {
            // try to preserve the selected item
            // restore which column was selected
            if (previousSelection !== null) {
                this.flexGrid.selection = new CellRange(
                    this.flexGrid.selection.row,
                    previousSelection.col,
                    this.flexGrid.selection.row2,
                    previousSelection.col2,
                )
            }
        }

        if (!this.initialSelectionSet && this.selectedItem == null) {
            if (this.view.items.length > 0 && this._doInitialSelection) {
                this.selectedItem = this.view.items[0]
                if (this.flexGrid) {
                    this.flexGrid.select(0, 0) // select the first item
                }
            } else {
                this.selectedItem = null
            }
            this.initialSelectionSet = true
        }
    }

    public get itemCount(): number {
        return this._view.items.length
    }

    public get itemTotalCount(): number {
        return this._view.sourceCollection.length
    }

    public get hasItemSelected(): boolean {
        return this._view.currentPosition >= 0
    }

    private _filter = ''
    public get filter(): string {
        return this._filter
    }
    public set filter(value: string) {
        this._filter = value
        this.filterRefresh()
    }

    public filterBy(getValues: (item: T) => any[]): Grid<T> {
        // Add our own filter to the collection view's filter arrary. We can't set our filter to the collection view's filter,
        // or it will be replaced when the grid filters are used.
        this._view.filters.push(item => {
            if (this._filter) {
                const filter = this._filter.toLowerCase()
                return getValues(item).some(value => value && value.toString().toLowerCase().indexOf(filter) >= 0)
            }
            return true
        })
        return this
    }

    private _flexGridFilter!: FlexGridFilter
    public get flexGridFilter(): FlexGridFilter {
        return this._flexGridFilter
    }

    private _initialized = new DataEvent<Grid<T>>()
    public get initialized(): IDataEvent<Grid<T>> {
        return this._initialized
    }

    public initialize(flexGrid: WjFlexGrid): void {
        if (this.isInitialized || flexGrid == undefined) return

        this.flexGrid = flexGrid
        flexGrid['controller'] = this.controller
        flexGrid.deferResizing = true
        flexGrid.allowMerging = AllowMerging.ColumnHeaders
        flexGrid.headersVisibility = this._headerVisibility
        flexGrid.selectionMode = this._selectionMode
        flexGrid.isReadOnly = this._isReadOnly
        flexGrid.allowDragging = this._allowDragging
        flexGrid.autoGenerateColumns = this._autoGenerateColumns
        flexGrid.allowSorting = this._allowSorting

        if (this._showAggregateRow) {
            flexGrid.columnFooters.rows.push(new GroupRow())
        }

        // wrap column headers
        if (this._columnHeaderRowHeight != undefined) {
            flexGrid.columnHeaders.rows[0].wordWrap = true
            flexGrid.columnHeaders.rows[0].height = this._columnHeaderRowHeight
            flexGrid.columnHeaders.rows.defaultSize = this._columnHeaderRowHeight
        }

        if (this._hasColumnFilters) {
            this._flexGridFilter = new FlexGridFilter(flexGrid)
        }

        const hostElement: HTMLElement = flexGrid.hostElement

        // if clicking in whitespace, remove selection
        const onClick = (event: MouseEvent) => {
            const hitTest: HitTestInfo = this.flexGrid.hitTest(event)
            if (!hitTest.range.isValid) {
                this.selectedItem = null
            }
            const col = hitTest.getColumn()

            // If the user clicks on a notes cell, we can show the whole notes in a tooltip.
            if (col && col.binding == 'notes') {
                const dataItem = hitTest.getRow().dataItem
                if (dataItem && dataItem.notes) {
                    const cellBounds = this.flexGrid.getCellBoundingRect(hitTest.row, hitTest.col)
                    this._tooltip.show(this.flexGrid.hostElement, dataItem.notes, cellBounds)
                }
            }
        }

        hostElement.addEventListener('click', onClick, false)
        if (this._editOnDoubleClick) {
            flexGrid.hostElement.addEventListener('dblclick', this.onDoubleClick.bind(this), false)
        }

        const onCurrentChanged = () => {
            if (this.isLoadingData) return
            this.onSelectionChanged(this.selectedItem)
        }
        this._view.currentChanged.addHandler(onCurrentChanged)

        const onCellEditEnding = (flexGrid: WjFlexGrid, args: CellRangeEventArgs) => {
            if (args.cancel) {
                return
            }

            // if we are formatting to be an integer, make sure the actual value will be an integer by
            // taking the displayed value from the grid
            const column: Column = flexGrid.columns[args.col]
            if (column.format == 'n0' && flexGrid.activeEditor) {
                flexGrid.setCellData(args.row, args.col, flexGrid.activeEditor.value, true)
                flexGrid.activeEditor.value = flexGrid.getCellData(args.row, args.col, true)
            }
        }

        this.flexGrid.cellEditEnding.addHandler(onCellEditEnding as any)

        if (this._allowDragging == AllowDragging.Both) {
            const onDraggingRow = (s: FlexGrid, e: CellRangeEventArgs) => {
                this.dragIndex = e.row
                s.collectionView.moveCurrentToPosition(this.dragIndex)
            }
            const onDraggedRow = (s: FlexGrid, e: CellRangeEventArgs) => {
                const dropIndex = e.row,
                    arr = s.collectionView.items
                s.collectionView.deferUpdate(() => {
                    const item = arr[this.dragIndex]
                    arr.splice(this.dragIndex, 1)
                    arr.splice(dropIndex, 0, item)
                    for (let i = 0; i < arr.length; i++) {
                        arr[i].order = i
                    }
                    s.collectionView.moveCurrentToPosition(dropIndex)
                    this.onDragged()
                })
            }
            this.flexGrid.draggingRow.addHandler(onDraggingRow)
            this.flexGrid.draggedRow.addHandler(onDraggedRow)
        }

        const unwireEvents = () => {
            hostElement.removeEventListener('click', onClick)
            this._view.currentChanged.removeHandler(onCurrentChanged)
            this.flexGrid.cellEditEnding.removeHandler(onCellEditEnding as any)
            hostElement.removeEventListener('dblclick', this.onDoubleClick.bind(this))

            this.initialized.clear()
            this.itemAdded.clear()
            this.itemEdited.clear()
            this.itemsDeleted.clear()
            this.itemFailedtoSave.clear()
            this.modified.clear()
            this.childItemsLoadedFromRepository.clear()
            this.selectionChanged.clear()
            this.doubleClick.clear()
            this.filterChanged.clear()
            this.flexGrid.draggedColumn.removeAllHandlers()
            this.flexGrid.draggingRow.removeAllHandlers()
            this.flexGrid.draggedRow.removeAllHandlers()
        }
        this.unwireEvents = unwireEvents

        this._initialized.raise(this)
        this.isInitialized = true
    }

    private unwireEvents!: () => void
    public destroy(): void {
        if (!this.isInitialized) return

        if (this._parentGrid != null) {
            this._parentGrid.selectionChanged.off(this._parentSelectionChangedHandler)
        }

        this.unwireEvents()
        this.isInitialized = false
    }
    private restoreSelection(key: string): boolean {
        if (this.items && key != undefined) {
            const foundItems = this.items.filter((o: any) => o[this.keyName] == key)
            if (foundItems.length > 0) {
                this.selectedItem = foundItems[0]
                return true
            }
        }
        return false
    }
    public asDetailsOf<TParent>(parentGrid: Grid<TParent>, getDetailItems: (parentItem: TParent) => T[]): Grid<T> {
        this._parentGrid = parentGrid
        this._getDetailItems = getDetailItems
        let parentPreviousSelection: any = null

        this._parentSelectionChangedHandler = parentGrid.selectionChanged.on((parentItem: TParent | any) => {
            const key = this.selectedItem && this.keyName ? this.selectedItem[this.keyName] : undefined

            if (
                parentGrid.keyName == undefined ||
                parentItem == null ||
                parentPreviousSelection == null ||
                parentItem[parentGrid.keyName] != parentPreviousSelection[parentGrid.keyName]
            ) {
                this.selectedItem = null
                this.initialSelectionSet = false
            }

            this.refresh()
            this.restoreSelection(key) // restore selection if possible (if the item belongs in multiple parents)

            parentPreviousSelection = parentItem
        })

        return this
    }
    public asDetailsOfRepository<TParent>(
        parentGrid: Grid<TParent>,
        repository: IRepository<T>,
        getParentId: (item: TParent) => any,
        getDetailItems: (parentItem: TParent) => T[],
    ): Grid<T> {
        this._parentGrid = parentGrid
        this._detailsOfRepository = repository
        this._getParentId = getParentId
        this._getDetailItems = getDetailItems
        this._parentItemsFetched = new Dictionary<boolean>()

        let parentPreviousSelection: any = null

        this._parentSelectionChangedHandler = parentGrid.selectionChanged.on((parentItem: TParent | any) => {
            const key = this.selectedItem && this.keyName ? this.selectedItem[this.keyName] : undefined

            if (
                parentGrid.keyName == undefined ||
                parentItem == null ||
                parentPreviousSelection == null ||
                parentItem[parentGrid.keyName] != parentPreviousSelection[parentGrid.keyName]
            ) {
                this.selectedItem = null
            }

            if (parentGrid.isLoadingData) return

            if (parentItem) {
                this.initialSelectionSet = false
                const parentId = getParentId(parentItem)

                // have we already fetched the details?
                if (this._parentItemsFetched.hasKey(parentId)) {
                    this.refresh()
                    this.restoreSelection(key) // restore selection if possible (if the item belongs in multiple parents)
                } else {
                    this._parentItemsFetched.add(parentId, false)

                    const params: URLSearchParams = new URLSearchParams()
                    params.append('parentId', parentId)

                    this.services.data.load(true, true, repository.queryFromParent({ search: params })).subscribe({
                        next: result => {
                            this.onChildItemsLoadedFromRepository(result[0])
                            this.initialSelectionSet = false
                            this.refresh()
                            this.restoreSelection(key) // restore selection if possible (if the item belongs in multiple parents)
                        },
                    })
                }
            } else {
                this.refresh()
                this.restoreSelection(key) // restore selection if possible (if the item belongs in multiple parents)
            }

            parentPreviousSelection = parentItem
        })

        return this
    }

    public refresh(keepOriginalSelection = false) {
        const items: T[] = this.items
        if (this._parentGrid != null) {
            items.clearArray()

            if (this._parentGrid.hasItemSelected) {
                this._getDetailItems(this._parentGrid.selectedItem).forEach(x => items.push(x))
            }
        }

        this.loadItems(items, keepOriginalSelection)
    }
    public wrapColumnHeaders(rowHeight = 55): Grid<T> {
        this._columnHeaderRowHeight = rowHeight
        return this
    }
    public isReadOnly(value = true): Grid<T> {
        this._isReadOnly = value
        return this
    }
    public doInitialSelection(value = true): Grid<T> {
        this._doInitialSelection = value
        return this
    }
    public showAggregateRow(value = true): Grid<T> {
        this._showAggregateRow = value
        return this
    }
    public allowDragging(value = AllowDragging.None): Grid<T> {
        if (value === AllowDragging.Both) {
            this._allowSorting = AllowSorting.None
            this._headerVisibility = HeadersVisibility.All
        }
        this._allowDragging = value
        return this
    }
    public selectionMode(value = SelectionMode.CellRange): Grid<T> {
        this._selectionMode = value
        return this
    }
    public allowSorting(value = AllowSorting.MultiColumn): Grid<T> {
        this._allowSorting = value
        return this
    }
    public headerVisibility(value = HeadersVisibility.Column): Grid<T> {
        this._headerVisibility = value
        return this
    }

    public autoGenerateColumns(value = false): Grid<T> {
        this._autoGenerateColumns = value
        return this
    }

    public withColumnFilters(): Grid<T> {
        this._hasColumnFilters = true
        return this
    }

    public editOnDoubleClick(value = true): Grid<T> {
        this._editOnDoubleClick = value
        return this
    }

    public setupModalEditing(
        repository: IRepository<T>,
        typeName: string,
        component: any,
        getItemToAdd?: () => T,
        initializeParams: any = {},
        size: ModalSize = ModalSize.default,
        backdrop: ModalBackDrop = ModalBackDrop.darkAndInactive,
    ): Grid<T> {
        this._modalRepository = repository
        this._modalComponent = component
        this._modalInitializeParams = initializeParams
        this._modalTypeName = typeName
        this._modalSize = size
        this._modalBackDrop = backdrop
        this._modalGetItemToAdd = getItemToAdd as any

        return this
    }
    public setupDeleteOnly(repository: IRepository<T>): Grid<T> {
        this._modalRepository = repository
        this.canAdd = false
        this.canEdit = false

        return this
    }

    public setupCustomDeletePrompt(prompt, resolution) {
        this._customDeletePrompt = prompt
        this._customDeletePromptResolution = resolution
        return this
    }

    public setupDeleteConfirm(title: string, message: string, multiTitle?: string, multiMessage?: string): Grid<T> {
        this._deleteConfirmTitle = title
        this._deleteConfirmMessage = message
        if (multiTitle) this._deleteConfirmMultiTitle = multiTitle
        if (multiMessage) this._deleteConfirmMultiMessage = multiMessage
        return this
    }

    private displayModalForEdit(target: T, editMode: ModalEditMode): Promise<any> {
        // copy the item, so we dont mess with the original
        const copy = this.services.utility.copy(target)

        // setup needed properties for the modal dialog
        const modalEditOptions: IModalEditOptions<T> = {
            target: copy,
            initializeParams: this._modalInitializeParams,
            mode: editMode,
            repository: this._modalRepository,
            typeName: this._modalTypeName,
        }

        // open the dialog
        const ref = this.services.modals.open(this._modalComponent, this._modalSize, this._modalBackDrop)
        const repoModal: RepositoryModal<T> = ref.componentInstance
        if (repoModal.initialize) {
            repoModal.initialize(modalEditOptions)
        }

        ref.result.then(
            saveResult => {
                if (editMode == ModalEditMode.adding || editMode == ModalEditMode.duplicating || editMode == ModalEditMode.importing) {
                    this.onItemAdded(saveResult)
                } else if (editMode == ModalEditMode.editing) {
                    this.onItemEdited(target, saveResult)
                }

                // refresh the grid
                this.refresh()
                this.selectedItem = saveResult
            },
            () => {
                this.onItemFailedtoSave(target)
            },
        )

        return ref.result
    }
    public addItem(duplicating = false): Promise<any> | null {
        if (!this.canAdd) {
            return null
        }
        const item = duplicating ? (this.services.utility.copy(this.selectedItem) as T) : this._modalGetItemToAdd()
        return this.displayModalForEdit(item, duplicating ? ModalEditMode.duplicating : ModalEditMode.adding)
    }

    public addItemWith(item: T): Promise<any> | null {
        if (!this.canAdd) {
            return null
        }
        return this.displayModalForEdit(item, ModalEditMode.adding)
    }
    public editItem(target?: T): Promise<any> | null {
        if (!this.canEdit) {
            return null
        }

        if (!target) {
            target = this.selectedItem
        }

        const result = this.onBeforeItemEdit(target as any)
        if (result.handled) {
            let success: (result?: any) => void

            const promise = new Promise<any>(resolve => {
                success = resolve
                success()
            })

            return promise
        }

        return this.displayModalForEdit(target as any, ModalEditMode.editing)
    }
    public deleteItem(target?: T | undefined): void {
        if (!this.canDelete) {
            return
        }

        if (this.flexGrid.selectedRows.length > 1) {
            this.services.modals.confirm(this._deleteConfirmMultiTitle, this._deleteConfirmMultiMessage).then(() => {
                const deletedItems = this.flexGrid.selectedRows.map(r => r.dataItem as T)
                const observables = [] as Observable<T>[]
                for (const item of this.flexGrid.selectedRows) {
                    observables.push(this._modalRepository.delete(item.dataItem))
                }
                const combinedObservable = forkJoin(observables)
                combinedObservable.subscribe({
                    error: err => {
                        this.services.notification.error(
                            'An error occured during the deletion of one or more items. Please refresh the page.',
                        )
                        console.log('Grid deleteItem error: ', err)
                    },
                    complete: () => {
                        this.onItemsDeleted(deletedItems)
                        this.refresh()
                    },
                })
            })
        } else {
            let currentTargetPosition = -1
            if (!target) {
                target = this.selectedItem
                currentTargetPosition = this._view.currentPosition
            }

            const deleteAction = () => {
                this._modalRepository.delete(target).subscribe({
                    next: () => {
                        this.onItemsDeleted([target as any])
                    },
                    error: errorResult => {
                        if (errorResult.status && errorResult.status == 400) {
                            const bodyText = errorResult.error
                            if (bodyText.length != 0) {
                                this.services.modals.warning('Cannot Delete', bodyText, ModalSize.default)
                            } else {
                                this.services.modals.warning(
                                    'Cannot Delete',
                                    'The record could not be deleted. It may be referenced elsewhere in the applicaton.',
                                )
                            }
                        } else {
                            this.services.notification.error('Failed to delete item')
                        }
                        this.refresh() // the item will be added back into the cache
                        this.selectedItem = target // reselect the item
                    },
                })

                // update the view
                this.refresh()

                // select a new item automatically for the user
                const newPosition = Math.min(currentTargetPosition, this.items.length - 1)
                if (newPosition >= 0) {
                    this.selectedItem = this.items[newPosition]
                } else {
                    if (this.selectedItem == null) {
                        this.onSelectionChanged(null)
                    } else {
                        this.selectedItem = null
                    }
                }
            }

            if (this._customDeletePrompt != undefined) {
                this._customDeletePrompt(target).then(() => {
                    deleteAction()
                })
            } else {
                this.services.modals.confirm(this._deleteConfirmTitle, this._deleteConfirmMessage).then(() => {
                    deleteAction()
                })
            }
        }
    }

    private _itemAdded = new DataEvent<T>()
    public get itemAdded(): IDataEvent<T> {
        return this._itemAdded
    }
    protected onItemAdded(item: T) {
        this._itemAdded.raise(item)

        if (this._detailsOfRepository) {
            this.onChildItemsLoadedFromRepository([item])
        }

        this.onModified()
    }

    private _beforeItemEdit = new DataEvent<GridBeforeEditEventArgs<T>>()
    public get beforeItemEdit(): IDataEvent<GridBeforeEditEventArgs<T>> {
        return this._beforeItemEdit
    }
    protected onBeforeItemEdit(item: T) {
        const args = new GridBeforeEditEventArgs<T>()
        args.handled = false
        args.item = item

        this._beforeItemEdit.raise(args)
        return args
    }

    private _itemEdited = new DataEvent<GridEditedEventArgs<T>>()
    public get itemEdited(): IDataEvent<GridEditedEventArgs<T>> {
        return this._itemEdited
    }
    protected onItemEdited(oldItem: T, newItem: T) {
        const args = new GridEditedEventArgs<T>()
        args.oldItem = oldItem
        args.newItem = newItem

        this._itemEdited.raise(args)

        if (this._detailsOfRepository) {
            this.onChildItemsLoadedFromRepository([newItem])
        }

        this.onModified()
    }

    private _itemsDeleted = new DataEvent<T[]>()
    public get itemsDeleted(): IDataEvent<T[]> {
        return this._itemsDeleted
    }
    protected onItemsDeleted(items: T[]) {
        this._itemsDeleted.raise(items)
        this.onModified()
    }

    private _modified = new Event()
    public get modified(): IEvent {
        return this._modified
    }
    protected onModified() {
        this._modified.raise()
    }

    private _dragged = new Event()
    public get dragged(): IEvent {
        return this._dragged
    }
    protected onDragged() {
        this._dragged.raise()
    }

    private _selectionChanged = new DataEvent<T>()
    public get selectionChanged(): IDataEvent<T> {
        return this._selectionChanged
    }
    public onSelectionChanged(item: T | null) {
        this._selectionChanged.raise(item as any)
    }

    private _itemFailedtoSave = new DataEvent<T>()
    public get itemFailedtoSave(): IDataEvent<T> {
        return this._itemFailedtoSave
    }
    protected onItemFailedtoSave(item: T) {
        this._itemFailedtoSave.raise(item)
    }

    private _childItemsLoadedFromRepository = new DataEvent<T[]>()
    public get childItemsLoadedFromRepository(): IDataEvent<T[]> {
        return this._childItemsLoadedFromRepository
    }
    protected onChildItemsLoadedFromRepository(items: T[]) {
        this._childItemsLoadedFromRepository.raise(items)
    }

    private _doubleClick = new DataEvent<GridDoubleClickEventArgs>()
    public get doubleClick(): IDataEvent<GridDoubleClickEventArgs> {
        return this._doubleClick
    }
    protected onDoubleClick(event: MouseEvent) {
        const args = new GridDoubleClickEventArgs()
        args.handled = false
        args.hitTest = this.flexGrid.hitTest(event)

        this._doubleClick.raise(args)
        if (!args.handled && args.hitTest.cellType == CellType.Cell && this._modalComponent != null && this.canEdit) {
            this.editItem()
        }
    }

    private _filterChanged = new Event()
    public get filterChanged(): IEvent {
        return this._filterChanged
    }
    protected onFilterChanged() {
        this._filterChanged.raise()
    }
}

export class GridBeforeEditEventArgs<T> {
    handled!: boolean
    item!: T
}

export class GridEditedEventArgs<T> {
    oldItem!: T
    newItem!: T
}

export class GridDoubleClickEventArgs {
    handled!: boolean
    hitTest!: HitTestInfo
}
